/**
 * The studio raises a quote itself. R-275.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 *
 * The quote queue had one entry point: the storefront form at `/custom/quote`.
 * `STOREFRONT_OPEN` is false, so that form is behind a closed door, and there
 * was therefore no way to create a quote at all.
 *
 * Every B2B job in this system starts at a quote and walks the same path: price
 * it, proof it, approve it, take a deposit, make it, invoice it. With no way to
 * start one, none of that path can be reached, and the Xero bridge has carried
 * zero documents since it was connected. Real work arrives by telephone and by
 * email anyway, not through a web form. This is the path for that.
 *
 * ── WHAT IT DOES NOT DO ─────────────────────────────────────────────────────
 *
 * It does not take a price. The estimate shown here is the same engine the
 * storefront shows a customer, and the server recomputes it from the rate card
 * before the row is written (constitution article 1). The CONFIRMED price is
 * set later, on the quote, by the step that already exists and already tells
 * the customer. So staff type what the job is, never what it costs.
 *
 * It also sends no email. The customer has not asked for anything yet, so a
 * "we received your request" message would be about something they never sent.
 */

import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';

import { EstimatePanel } from '@/components/studio/estimate-panel';
import { Button, Callout, Field, inputClass, textareaClass } from '@/components/ui';
import { methodRowToEngine } from '@/lib/data/seed';
import { LOCATIONS, computeEstimate } from '@/lib/pricing/engine';
import { adminShellQuery, newQuoteQuery } from '@/queries';
import type { LocationKey } from '@/lib/pricing/engine';

interface Created { ref: string; quote_id: string }

export function NewQuote({ forProspect }: { forProspect?: string } = {}) {
  // "Start a quote" on a prospect lands here with the form already open.
  const [open, setOpen] = useState(Boolean(forProspect));

  if (!open) {
    return (
      <Button onClick={() => setOpen(true)}>New quote</Button>
    );
  }
  // Full width on its own row: the button sits beside the heading, and the
  // form it opens is a form, not a control in a header.
  return (
    <div className="w-full">
      <Form onClose={() => setOpen(false)} forProspect={forProspect} />
    </div>
  );
}

/** One result of the customer search: a prospect or a customer. Spec 009 D-6. */
interface Person {
  kind: 'prospect' | 'customer';
  id: string;
  name: string;
  contact_name?: string | null;
  email: string | null;
  phone: string | null;
  suburb?: string | null;
}

function Form({ onClose, forProspect }: { onClose: () => void; forProspect?: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  /**
   * The catalogue, read only once the panel is open.
   *
   * 381 garments is a real read, and the register above is a queue somebody
   * refreshes all day. Loading the catalogue to draw a button nobody pressed
   * would make the queue slower for the sake of this form.
   */
  const catalogue = useQuery(newQuoteQuery);

  const [productId, setProductId] = useState('');
  const [methodKey, setMethodKey] = useState('');
  const [search, setSearch] = useState('');
  const [qty, setQty] = useState('24');
  const [colours, setColours] = useState('1');
  const [locations, setLocations] = useState<LocationKey[]>(['front']);
  const [color, setColor] = useState('');
  const [size, setSize] = useState('');
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Who this quote is for. Spec 009 D-6: a prospect, a customer, or nobody yet,
  // in which case the new name becomes a prospect when the quote saves.
  const [linked, setLinked] = useState<Person | null>(null);
  const [lookFor, setLookFor] = useState('');
  const [picking, setPicking] = useState(false);
  const { can } = useSuspenseQuery(adminShellQuery).data;
  const mayAddProspect = (can.prospects ?? []).includes('create');

  const people = useQuery({
    queryKey: ['admin', 'people-search', lookFor],
    queryFn: async () => {
      const res = await fetch(`/api/people-search?q=${encodeURIComponent(lookFor)}`, { credentials: 'same-origin' });
      if (!res.ok) return { results: [] as Person[] };
      return (await res.json()) as { results: Person[] };
    },
    enabled: picking && !linked && lookFor.trim().length >= 2,
    staleTime: 10_000,
  });

  // Debounced, so a name typed quickly is one search rather than one per key.
  useEffect(() => {
    const t = setTimeout(() => setLookFor(name.trim()), 250);
    return () => clearTimeout(t);
  }, [name]);

  function pickPerson(p: Person) {
    setLinked(p);
    setPicking(false);
    // FR-920b: the quote's own copy. Changing it here does not change the record.
    if (p.kind === 'prospect') {
      if (p.contact_name) { setName(p.contact_name); setCompany(p.name); }
      else { setName(p.name); }
    } else {
      setName(p.name);
    }
    setEmail(p.email ?? '');
    setPhone(p.phone ?? '');
  }

  // "Start a quote" from a prospect: fill the form from it.
  useEffect(() => {
    if (!forProspect) return;
    void (async () => {
      const res = await fetch(`/api/prospects/${forProspect}`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const { prospect: p } = (await res.json()) as { prospect: {
        id: string; name: string; contact_name: string | null; email: string | null;
        phone: string | null; suburb: string | null; garment_interest: string | null;
        decoration_notes: string | null;
      } };
      pickPerson({ kind: 'prospect', id: p.id, name: p.name, contact_name: p.contact_name,
        email: p.email, phone: p.phone, suburb: p.suburb });
      const brief = [p.garment_interest && `Garments: ${p.garment_interest}`,
        p.decoration_notes && `Decoration: ${p.decoration_notes}`].filter(Boolean).join('\n');
      if (brief) setNotes((n) => n || brief);
    })();
    // Once, for the prospect named in the address.
  }, [forProspect]);

  const products = catalogue.data?.products ?? [];
  const methods = catalogue.data?.methods ?? [];

  const shown = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = needle
      ? products.filter((p) => p.name.toLowerCase().includes(needle))
      : products;
    // The chosen garment stays in the list whatever the filter says, so the
    // selection cannot be silently lost by typing in the box next to it.
    const picked = products.find((p) => p.id === productId);
    return picked && !list.some((p) => p.id === picked.id) ? [picked, ...list] : list;
  }, [products, search, productId]);

  const product = products.find((p) => p.id === productId) ?? null;
  const method = methods.find((m) => m.key === methodKey) ?? null;

  /**
   * Has anybody priced this decoration yet?
   *
   * All five of the client's methods read zero on every rate, so all five are
   * switched off: `no_quotable_method_without_rates` will not let a method be
   * quotable at no price. That is correct, and it means the estimate here
   * covers the garment alone. Saying so is the difference between a preview
   * and a wrong number.
   */
  const unpriced = method != null
    && method.setup_per_colour_per_location_cents === 0
    && method.setup_per_location_cents === 0
    && method.print_per_location_cents === 0
    && method.extra_colour_cost_cents === 0;

  /**
   * The estimate, drawn from the same engine the customer sees.
   *
   * Only once a garment and a method are chosen: an estimate over no garment
   * would be an estimate of the decoration alone, which is a number nobody
   * quotes and everybody would read as the price.
   */
  const estimate = product && method
    ? computeEstimate(
        product.base_price_cents / 100,
        methodRowToEngine(method),
        { qty, colours, locations },
      )
    : null;

  const create = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/quote-create', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          contact: {
            name: name.trim(),
            company: company.trim() || undefined,
            email: email.trim(),
            phone: phone.trim() || undefined,
          },
          product_id: productId,
          method_key: methodKey,
          color: color || null,
          size: size || null,
          qty: Number(qty),
          colours: Number(colours) || 1,
          locations,
          notes: notes.trim() || null,
          prospect_id: linked?.kind === 'prospect' ? linked.id : null,
          customer_id: linked?.kind === 'customer' ? linked.id : null,
        }),
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Request failed (${res.status})`);
      }
      return (await res.json()) as Created;
    },
    onSuccess: async (made) => {
      setError(null);
      /**
       * `quote-list`, which is what the register reads, AND `quotes`, which
       * the admin shell reads for the navigation count. Invalidating only
       * `['admin','quotes']` would leave the queue showing the old rows behind
       * the new quote, which reads as a quote that was not saved.
       */
      await qc.invalidateQueries({ queryKey: ['admin', 'quote-list'] });
      await qc.invalidateQueries({ queryKey: ['admin', 'quotes'] });
      // Straight to the quote, because the next act is to price it.
      await navigate({ to: '/admin/quotes/$ref', params: { ref: made.ref } });
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!name.trim()) return setError('Name the person to reply to.');
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      return setError('Use an email address the studio can reply to.');
    }
    if (!productId) return setError('Choose a garment.');
    if (!methodKey) return setError('Choose a decoration method.');
    if (!(Number(qty) > 0)) return setError('Give a quantity of at least one.');
    setError(null);
    create.mutate();
  }

  function toggleLocation(key: LocationKey) {
    setLocations((was) =>
      was.includes(key) ? was.filter((k) => k !== key) : [...was, key],
    );
  }

  return (
    <form
      onSubmit={submit}
      className="rounded-[10px] border border-line bg-panel p-5"
      aria-label="Raise a quote"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold">Raise a quote</h2>
          <p className="mt-1 text-[12.5px] text-muted">
            For a job that came in by telephone or by email. The estimate is a
            preview. You set the confirmed price on the quote.
          </p>
        </div>
        <Button type="button" weight="secondary" onClick={onClose}>Cancel</Button>
      </div>

      {error && <div className="mt-4"><Callout tone="danger" title={error} /></div>}
      {catalogue.isError && (
        <div className="mt-4">
          <Callout tone="danger" title="The catalogue did not load, so there is nothing to choose from." />
        </div>
      )}

      <div className="mt-5 grid gap-5 lg:grid-cols-[1.4fr_1fr]">
        <div className="grid gap-5 sm:grid-cols-2">
          <Field
            label="Customer" htmlFor="nq-name"
            hint={linked
              ? undefined
              : 'Type a name to find a prospect or a customer.'}
          >
            <div className="relative">
              <input
                id="nq-name" className={inputClass} value={name}
                onChange={(e) => { setName(e.target.value); setPicking(true); }}
                onFocus={() => setPicking(true)}
                onBlur={() => setTimeout(() => setPicking(false), 150)}
                autoComplete="off" role="combobox" aria-expanded={picking && (people.data?.results.length ?? 0) > 0}
                aria-controls="nq-people"
              />
              {picking && !linked && (people.data?.results.length ?? 0) > 0 && (
                <ul
                  id="nq-people" role="listbox"
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-[260px] overflow-auto rounded-[8px] border border-line bg-panel shadow-lg"
                >
                  {people.data!.results.map((p) => (
                    <li key={`${p.kind}-${p.id}`} role="option" aria-selected="false">
                      <button
                        type="button"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => pickPerson(p)}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] hover:bg-raised"
                      >
                        <span className="rounded-[4px] bg-raised px-1.5 text-[11px] text-muted">
                          {p.kind === 'prospect' ? 'Prospect' : 'Customer'}
                        </span>
                        <span className="max-w-[55%] shrink-0 truncate font-medium">{p.name}</span>
                        <span className="min-w-0 truncate text-muted">
                          {[p.contact_name, p.suburb, p.email, p.phone].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {linked ? (
              <p className="mt-1 text-[12px]">
                For {linked.kind === 'prospect' ? 'prospect' : 'customer'} <strong>{linked.name}</strong>.
                {' '}Changes here stay on this quote.{' '}
                <button type="button" className="underline" onClick={() => setLinked(null)}>Not them</button>
              </p>
            ) : name.trim().length >= 2 && mayAddProspect && !people.isFetching
              && (people.data?.results.length ?? 0) === 0 ? (
              <p className="mt-1 text-[12px] text-muted">New. Saving this quote adds them to Prospects.</p>
            ) : null}
          </Field>
          <Field label="Company" htmlFor="nq-company" optional>
            <input
              id="nq-company" className={inputClass} value={company}
              onChange={(e) => setCompany(e.target.value)} autoComplete="off"
            />
          </Field>
          <Field label="Email" htmlFor="nq-email" hint="Where the priced quote goes.">
            <input
              id="nq-email" type="email" className={inputClass} value={email}
              onChange={(e) => setEmail(e.target.value)} autoComplete="off"
            />
          </Field>
          <Field label="Phone" htmlFor="nq-phone" optional>
            <input
              id="nq-phone" className={inputClass} value={phone}
              onChange={(e) => setPhone(e.target.value)} autoComplete="off"
            />
          </Field>

          <Field
            label="Garment" htmlFor="nq-product"
            hint={catalogue.isPending ? 'Loading the catalogue...' : `${products.length} active garments.`}
          >
            <input
              className={inputClass + ' mb-2'} placeholder="Filter by name"
              value={search} onChange={(e) => setSearch(e.target.value)}
              aria-label="Filter garments by name"
            />
            <select
              id="nq-product" className={inputClass} value={productId}
              onChange={(e) => { setProductId(e.target.value); setColor(''); setSize(''); }}
            >
              <option value="">Choose a garment</option>
              {shown.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>

          <Field label="Decoration" htmlFor="nq-method">
            <select
              id="nq-method" className={inputClass} value={methodKey}
              onChange={(e) => setMethodKey(e.target.value)}
            >
              <option value="">Choose a method</option>
              {methods.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.name}
                  {m.setup_per_colour_per_location_cents === 0
                    && m.setup_per_location_cents === 0
                    && m.print_per_location_cents === 0
                    ? ' (no rates set)'
                    : ''}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Quantity" htmlFor="nq-qty">
            <input
              id="nq-qty" className={inputClass} inputMode="numeric" value={qty}
              onChange={(e) => setQty(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </Field>

          {/* Colours only when the method charges for them. A full colour
              method has no colour count, and a box that changes nothing is a
              box somebody fills in and then distrusts. */}
          {method?.max_colours != null ? (
            <Field
              label="Colours" htmlFor="nq-colours"
              hint={`Up to ${method.max_colours}.`}
            >
              <input
                id="nq-colours" className={inputClass} inputMode="numeric" value={colours}
                onChange={(e) => setColours(e.target.value.replace(/[^0-9]/g, ''))}
              />
            </Field>
          ) : (
            <div />
          )}

          {product && product.colors.length > 0 && (
            <Field label="Garment colour" htmlFor="nq-color" optional>
              <select
                id="nq-color" className={inputClass} value={color}
                onChange={(e) => setColor(e.target.value)}
              >
                <option value="">Not decided</option>
                {product.colors.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </Field>
          )}

          {product && product.sizes.length > 0 && (
            <Field label="Size" htmlFor="nq-size" optional>
              <select
                id="nq-size" className={inputClass} value={size}
                onChange={(e) => setSize(e.target.value)}
              >
                <option value="">Mixed</option>
                {product.sizes.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </Field>
          )}

          <fieldset className="sm:col-span-2">
            <legend className="text-sm font-semibold">Print positions</legend>
            <div className="mt-2 flex flex-wrap gap-3">
              {LOCATIONS.map((l) => (
                <label key={l.key} className="flex items-center gap-2 text-[13px]">
                  <input
                    type="checkbox" checked={locations.includes(l.key)}
                    onChange={() => toggleLocation(l.key)}
                  />
                  {l.name}
                </label>
              ))}
            </div>
          </fieldset>

          <div className="sm:col-span-2">
            <Field label="Notes" htmlFor="nq-notes" optional
              hint="What was agreed on the call. It travels with the quote."
            >
              <textarea
                id="nq-notes" className={textareaClass} value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
            </Field>
          </div>
        </div>

        <div>
          {estimate ? (
            <>
              <EstimatePanel local={estimate} server={null} reconciling={false} />
              {unpriced && (
                <div className="mt-3">
                  <Callout tone="warn" title="No decoration rates are set for this method">
                    The estimate covers the garment only. Set the rate card on
                    Decoration methods, or price this quote by hand once it is
                    raised.
                  </Callout>
                </div>
              )}
            </>
          ) : (
            <p className="rounded-[10px] border border-line bg-raised p-5 text-[13px] text-muted">
              Choose a garment and a decoration method and the estimate appears
              here.
            </p>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? 'Raising...' : 'Raise the quote'}
        </Button>
        <span className="text-[13px] text-muted">
          It lands in Needs action. The customer is not emailed until you price
          it.
        </span>
      </div>
    </form>
  );
}
